import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { bibleApi } from "./bible-api";
import { BibleVersion, BibleBook, BibleChapter, BibleVerse, Language } from "./types";

export default function BibleVerseOccasion() {
  const [versions, setVersions] = useState<BibleVersion[]>([]);
  const [books, setBooks] = useState<BibleBook[]>([]);
  const [chapters, setChapters] = useState<BibleChapter[]>([]);
  const [verses, setVerses] = useState<BibleVerse[]>([]);
  const [languages, setLanguages] = useState<Language[]>([]);

  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const [selectedBook, setSelectedBook] = useState<number | null>(null);
  const [selectedChapter, setSelectedChapter] = useState<number | null>(null);
  const [selectedVerse, setSelectedVerse] = useState<number | null>(null);
  const [overrideText, setOverrideText] = useState<string>("");
  const [selectedLanguage, setSelectedLanguage] = useState<string>("");

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedLanguage) {
      filterVersionsByLanguage();
    } else {
      fetchVersions();
    }
  }, [selectedLanguage]);

  useEffect(() => {
    if (selectedBook) {
      fetchChapters(selectedBook);
    } else {
      setChapters([]);
      setVerses([]);
      setSelectedChapter(null);
      setSelectedVerse(null);
    }
  }, [selectedBook]);

  useEffect(() => {
    if (selectedBook && selectedChapter && selectedVersion) {
      fetchVerses();
    } else {
      setVerses([]);
      setSelectedVerse(null);
    }
  }, [selectedBook, selectedChapter, selectedVersion]);

  useEffect(() => {
    if (selectedVerse && verses.length > 0) {
      const verse = verses.find((v) => v.verse_index === selectedVerse);
      if (verse) {
        setOverrideText(verse.text);
      }
    } else {
      setOverrideText("");
    }
  }, [selectedVerse, verses]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [languagesData] = await Promise.all([bibleApi.getLanguages()]);
      setLanguages(languagesData);
      await fetchVersions();
      await fetchBooks();
    } catch (error: any) {
      toast.error("Failed to load initial data");
    } finally {
      setLoading(false);
    }
  };

  const fetchVersions = async () => {
    try {
      const versionsData = await bibleApi.getAllVersions();
      setVersions(versionsData);
    } catch (error: any) {
      toast.error("Failed to load Bible versions");
    }
  };

  const filterVersionsByLanguage = async () => {
    try {
      const allVersions = await bibleApi.getAllVersions();
      const filtered = allVersions.filter((v) => v.languageCode === selectedLanguage);
      setVersions(filtered);
      if (selectedVersion && !filtered.find((v) => v.id === selectedVersion)) {
        setSelectedVersion(null);
        setSelectedBook(null);
        setSelectedChapter(null);
        setSelectedVerse(null);
        setVerses([]);
      }
    } catch (error: any) {
      toast.error("Failed to filter versions");
    }
  };

  const fetchBooks = async () => {
    try {
      const booksData = await bibleApi.getAllBooks();
      setBooks(booksData);
    } catch (error: any) {
      toast.error("Failed to load books");
    }
  };

  const fetchChapters = async (bookId: number) => {
    try {
      const chaptersData = await bibleApi.getChaptersByBook(bookId);
      setChapters(chaptersData);
      setSelectedChapter(null);
      setSelectedVerse(null);
      setVerses([]);
    } catch (error: any) {
      toast.error("Failed to load chapters");
    }
  };

  const fetchVerses = async () => {
    if (!selectedBook || !selectedChapter || !selectedVersion) return;

    setLoading(true);
    try {
      const versesData = await bibleApi.getVerses({
        versionIds: [selectedVersion],
        bookId: selectedBook,
        chapterNumber: selectedChapter,
      });
      // setVerses(versesData.sort((a, b) => a.verseNumber - b.verseNumber));
    } catch (error: any) {
      toast.error("Failed to load verses");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedVersion || !selectedBook || !selectedChapter || !selectedVerse) {
      toast.warning("Please select version, book, chapter, and verse");
      return;
    }

    if (!overrideText.trim()) {
      toast.warning("Please enter verse text");
      return;
    }

    // TODO: Implement save API call
    toast.success("Verse saved successfully!");
  };

  const selectedBookName = books.find((b) => b.id === selectedBook)?.name || "";
  const selectedVersionName = versions.find((v) => v.id === selectedVersion)?.name || "";

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-8 px-4">
      <div className="max-w-[1200px] mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Verse Page for Occasion
          </h1>
          <p className="text-gray-600 dark:text-gray-300">
            Select and customize Bible verses for occasions
          </p>
        </div>

        {/* Selection Form */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {/* Language Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Language
              </label>
              <select
                value={selectedLanguage}
                onChange={(e) => setSelectedLanguage(e.target.value)}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white"
              >
                <option value="">All Languages</option>
                {languages.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Version Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Version <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedVersion || ""}
                onChange={(e) => {
                  setSelectedVersion(e.target.value ? Number(e.target.value) : null);
                  setSelectedBook(null);
                  setSelectedChapter(null);
                  setSelectedVerse(null);
                }}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white"
              >
                <option value="">Select Version</option>
                {versions.map((version) => (
                  <option key={version.id} value={version.id}>
                    {version.name} {version.abbreviation && `(${version.abbreviation})`}
                  </option>
                ))}
              </select>
            </div>

            {/* Book Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Book <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedBook || ""}
                onChange={(e) => {
                  setSelectedBook(e.target.value ? Number(e.target.value) : null);
                  setSelectedChapter(null);
                  setSelectedVerse(null);
                }}
                disabled={!selectedVersion}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">Select Book</option>
                {books.map((book) => (
                  <option key={book.id} value={book.id}>
                    {book.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Chapter Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Chapter <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedChapter || ""}
                onChange={(e) => {
                  setSelectedChapter(e.target.value ? Number(e.target.value) : null);
                  setSelectedVerse(null);
                }}
                disabled={!selectedBook}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">Select Chapter</option>
                {chapters.map((chapter) => (
                  <option key={chapter.id} value={chapter.chapterNumber}>
                    Chapter {chapter.chapterNumber}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Verse Selection */}
          {verses.length > 0 && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Verse <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedVerse || ""}
                onChange={(e) => setSelectedVerse(e.target.value ? Number(e.target.value) : null)}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white"
              >
                <option value="">Select Verse</option>
                {verses.map((verse) => (
                  <option key={verse.verse_index} value={verse.verse_index}>
                    Verse {verse.verse_index}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Override Text */}
          {selectedVerse && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Verse Text (Override) <span className="text-red-500">*</span>
              </label>
              <textarea
                value={overrideText}
                onChange={(e) => setOverrideText(e.target.value)}
                rows={6}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter or modify the verse text..."
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Reference: {selectedBookName} {selectedChapter}:{selectedVerse} ({selectedVersionName})
              </p>
            </div>
          )}

          {/* Save Button */}
          {selectedVerse && (
            <div className="flex justify-end">
              <button
                onClick={handleSave}
                className="btn-primary px-6 py-2"
              >
                <i className="fa-solid fa-save mr-2"></i>
                Save Verse
              </button>
            </div>
          )}
        </div>

        {/* Preview */}
        {selectedVerse && overrideText && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Preview
            </h3>
            <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
              <p className="font-semibold text-blue-600 dark:text-blue-400 mb-2">
                {selectedBookName} {selectedChapter}:{selectedVerse}
              </p>
              <p className="text-gray-800 dark:text-gray-200 leading-relaxed">
                {overrideText}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                {selectedVersionName}
              </p>
            </div>
          </div>
        )}

        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        )}
      </div>
    </div>
  );
}

